import type { AudioCue } from './types';

export interface SynthTone {
  readonly waveform: OscillatorType;
  readonly fromHz: number;
  readonly toHz: number;
  readonly durationSeconds: number;
  readonly gain: number;
  readonly delaySeconds?: number;
  readonly attackSeconds?: number;
}

export interface SynthRecipeOptions {
  readonly reelIndex?: number;
  readonly step?: number;
  readonly total?: number;
  readonly phase?: 'clear' | 'deal';
  readonly turbo?: boolean;
}

const SCATTER_VARIANTS = Object.freeze([
  Object.freeze({ playbackRate: 1, sampleGain: 0.126, accentHz: 587.33, accentGain: 0.0072, shimmerGain: 0.0042 }),
  Object.freeze({ playbackRate: 2 ** (2 / 12), sampleGain: 0.121, accentHz: 659.25, accentGain: 0.0085, shimmerGain: 0.0048 }),
  Object.freeze({ playbackRate: 2 ** (3 / 12), sampleGain: 0.118, accentHz: 698.46, accentGain: 0.0096, shimmerGain: 0.0053 }),
]);

export interface ScatterSoundProfile {
  readonly stage: 1 | 2 | 3;
  readonly playbackRate: number;
  readonly sampleGain: number;
  readonly accentHz: number;
  readonly accentGain: number;
  readonly shimmerGain: number;
}

function tone(
  waveform: OscillatorType,
  fromHz: number,
  toHz: number,
  durationSeconds: number,
  gain: number,
  delaySeconds = 0,
  attackSeconds = 0.01,
): SynthTone {
  return Object.freeze({ waveform, fromHz, toHz, durationSeconds, gain, delaySeconds, attackSeconds });
}

export function getScatterSoundProfile(stageValue: number): ScatterSoundProfile {
  const stage = Math.max(1, Math.min(3, Math.trunc(stageValue))) as 1 | 2 | 3;
  return Object.freeze({ stage, ...SCATTER_VARIANTS[stage - 1]! });
}

export function createSynthCueRecipe(cue: AudioCue, options: SynthRecipeOptions = {}): readonly SynthTone[] {
  const reel = Math.max(0, Math.min(4, Math.trunc(options.reelIndex ?? 0)));
  switch (cue) {
    case 'button':
      return [tone('square', 420, 250, 0.06, 0.03)];
    case 'spin':
    case 'draw':
      return [
        tone('triangle', 178, 108, options.turbo === true ? 0.08 : 0.12, options.turbo === true ? 0.018 : 0.024, 0.01),
        tone('sine', 260, 194, options.turbo === true ? 0.07 : 0.1, options.turbo === true ? 0.01 : 0.014, 0.02),
      ];
    case 'card-flip': {
      const base = 316 + reel * 32;
      const upper = base * 1.55;
      return [
        tone('triangle', base, base * 1.055, 0.09, 0.027),
        tone('sine', base * 0.54, base * 0.43, 0.11, 0.012, 0.012),
        tone('sine', upper, upper * 1.06, 0.075, 0.014, 0.026),
        tone('triangle', upper * 1.18, upper * 1.18 * 1.03, 0.058, 0.009, 0.05),
      ];
    }
    case 'shuffle': {
      const base = options.phase === 'deal' ? 182 : 156;
      return [
        tone('triangle', base, base * 0.84, 0.1, 0.008),
        tone('sine', base * 2.45, base * 2.06, 0.075, 0.0045, 0.02),
      ];
    }
    case 'reel-stop': {
      const reelPitch = 1 + reel * 0.08;
      return [
        tone('triangle', 160 * reelPitch, 70, 0.11, 0.07),
        tone('sine', 520 * reelPitch, 420 * reelPitch, 0.09, 0.03, 0.01),
      ];
    }
    case 'line-step': {
      const total = Math.max(1, Math.trunc(options.total ?? 1));
      const progress = Math.min(1, Math.max(0, Math.trunc(options.step ?? 0)) / total);
      const base = 420 + progress * 250;
      return [
        tone('triangle', base, base * 1.06, 0.09, 0.04),
        tone('sine', base * 1.25, base * 1.34, 0.08, 0.028, 0.025),
      ];
    }
    case 'anticipation':
      return [
        tone('sawtooth', 280, 920, 0.18, 0.05),
        tone('triangle', 620, 1180, 0.14, 0.03, 0.04),
      ];
    case 'scatter-1':
    case 'scatter-2':
    case 'scatter-3': {
      const profile = getScatterSoundProfile(Number(cue.at(-1)));
      const base = profile.accentHz;
      return [
        tone('triangle', base, base * 1.024, 0.085, profile.accentGain, 0, 0.006),
        tone('sine', base * 1.49, base * 1.44, 0.075, profile.accentGain * 0.58, 0.018, 0.005),
        tone('triangle', base * 2.01, base * 2.06, 0.05, profile.shimmerGain, 0.035, 0.004),
      ];
    }
    case 'small-win':
      return [
        tone('sine', 720, 1020, 0.12, 0.045),
        tone('sine', 920, 1220, 0.12, 0.025, 0.04),
      ];
    case 'win':
      return [
        tone('sine', 392, 392, 0.16, 0.06),
        tone('sine', 523, 523, 0.16, 0.06, 0.07),
        tone('sine', 659, 659, 0.24, 0.06, 0.14),
      ];
    case 'big-win':
      return [
        tone('triangle', 330, 495, 0.2, 0.07),
        tone('triangle', 495, 660, 0.2, 0.07, 0.12),
        tone('triangle', 660, 880, 0.35, 0.08, 0.25),
      ];
    case 'jackpot':
      return [
        tone('triangle', 330, 495, 0.18, 0.08),
        tone('triangle', 495, 660, 0.18, 0.08, 0.12),
        tone('triangle', 660, 990, 0.3, 0.09, 0.24),
        tone('sine', 990, 1320, 0.36, 0.05, 0.38),
      ];
    case 'feature':
      return [
        tone('sawtooth', 260, 420, 0.14, 0.05),
        tone('sawtooth', 420, 720, 0.18, 0.06, 0.08),
        tone('triangle', 720, 1040, 0.32, 0.07, 0.2),
      ];
    case 'retrigger':
      return [
        tone('triangle', 392, 523.25, 0.2, 0.055),
        tone('triangle', 523.25, 783.99, 0.28, 0.065, 0.13),
        tone('sine', 783.99, 1046.5, 0.34, 0.055, 0.26),
      ];
    case 'count-up':
      return [tone('sine', 980, 860, 0.07, 0.02)];
    case 'wild-impact':
    case 'pot-activate':
      return [];
  }
}
